import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  XCircle,
  Eye,
  Package,
  FileText,
  Receipt,
  FolderPlus,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import { purchaseRequestApiService, PurchaseRequest } from '../../services/purchaseRequestApi';
import { projectApiService } from '../../services/projectApi';

const MobileDemandesClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  useEffect(() => {
    loadPurchaseRequests();
  }, []);

  const loadPurchaseRequests = async () => {
    try {
      setLoading(true);
      const requests = await purchaseRequestApiService.getAllPurchaseRequests();
      setPurchaseRequests(requests);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'confirmed':
        return {
          label: 'Confirmée',
          color: 'bg-green-100 text-green-800',
          icon: CheckCircle,
          bgColor: 'bg-green-500'
        };
      case 'revision':
        return {
          label: 'En révision',
          color: 'bg-amber-100 text-amber-800',
          icon: AlertCircle,
          bgColor: 'bg-amber-500'
        };
      case 'pending':
        return {
          label: 'En attente',
          color: 'bg-blue-100 text-blue-800',
          icon: Clock,
          bgColor: 'bg-blue-500'
        };
      case 'cancelled':
        return {
          label: 'Annulée',
          color: 'bg-red-100 text-red-800',
          icon: XCircle,
          bgColor: 'bg-red-500'
        };
      default:
        return {
          label: 'Inconnu',
          color: 'bg-gray-100 text-gray-800',
          icon: Clock,
          bgColor: 'bg-gray-500'
        };
    }
  };

  const formatPrice = (price: number | undefined | null) => {
    const safePrice = price || 0;
    return `${safePrice.toLocaleString('fr-FR')} FCFA`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const viewInvoice = (request: PurchaseRequest) => {
    navigate('/facture-proforma', {
      state: {
        purchaseRequestId: request.id,
        viewMode: true,
        invoiceData: request
      }
    });
  };

  const createProject = async (request: PurchaseRequest) => {
    if (request.status !== 'confirmed') {
      alert('Seules les factures confirmées peuvent être converties en projet');
      return;
    }

    if (!window.confirm(`Créer un projet pour la facture #${request.invoice_id || request.id} ?\n\nClient: ${request.client_name}\nMontant: ${formatPrice(request.total_amount)}`)) {
      return;
    }

    try {
      setLoading(true);
      if (!request.id) throw new Error('ID manquant');
      
      const project = await projectApiService.createProjectFromInvoice(request.id as number);
      alert(`✅ Projet créé !\n\nID: ${project.project_id}\nClient: ${project.client_name}`);
      loadPurchaseRequests();
    } catch (error) {
      console.error('Erreur création projet:', error);
      alert('Erreur lors de la création du projet');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = purchaseRequests.filter(request => {
    return selectedStatus === 'all' || request.status === selectedStatus;
  });

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 px-4 py-6 sticky top-0 z-10 shadow-lg">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-3"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Retour</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white mb-1">Demandes Clients</h1>
            <p className="text-sm text-white/90">{filteredRequests.length} demande(s)</p>
          </div>
          
          <button
            onClick={loadPurchaseRequests}
            disabled={loading}
            className="p-2 bg-white/20 rounded-lg text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filtre */}
        <div className="mt-4">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg text-sm border border-white/30 focus:outline-none focus:bg-white/30"
          >
            <option value="all" className="text-gray-900">Tous les statuts</option>
            <option value="pending" className="text-gray-900">En attente</option>
            <option value="revision" className="text-gray-900">En révision</option>
            <option value="confirmed" className="text-gray-900">Confirmées</option>
            <option value="cancelled" className="text-gray-900">Annulées</option>
          </select>
        </div>
      </div>

      {/* Liste des demandes */}
      <div className="px-4 py-6 space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune demande</h3>
            <p className="text-sm text-gray-500">
              {selectedStatus !== 'all' 
                ? 'Aucune demande avec ce statut.'
                : 'Aucune demande d\'achat.'
              }
            </p>
          </div>
        ) : (
          filteredRequests.map((request) => {
            const statusInfo = getStatusInfo(request.status);
            const StatusIcon = statusInfo.icon;
            const isExpanded = expandedCard === request.id;

            return (
              <div key={request.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* En-tête */}
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg ${statusInfo.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <StatusIcon className="w-5 h-5 text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <button
                          onClick={() => navigate(`/detailsdemande/${request.id}`)}
                          className="font-bold text-gray-900 text-sm text-left hover:text-blue-600"
                        >
                          Facture #{request.invoice_id || request.id}
                        </button>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color} flex-shrink-0`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span>{request.client_name || request.user_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(request.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          <span>{request.items?.length || 0} item(s)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Prix */}
                  <div className="bg-blue-50 rounded-lg p-3 mb-3">
                    <div className="text-xs text-gray-600 mb-1">Montant total</div>
                    <div className="text-lg font-bold text-blue-600">
                      {formatPrice(request.total_amount)}
                    </div>
                    {request.discount_percentage && Number(request.discount_percentage) > 0 && (
                      <div className="text-xs text-green-600 mt-1">
                        Réduction: -{request.discount_percentage}%
                      </div>
                    )}
                  </div>

                  {/* Items preview */}
                  {request.items && request.items.length > 0 && !isExpanded && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {request.items.slice(0, 2).map((item, index) => (
                          <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs text-gray-600">
                            {item.service_name || item.pack_name || `Item ${index + 1}`}
                          </div>
                        ))}
                        {request.items.length > 2 && (
                          <div className="bg-gray-200 px-2 py-1 rounded text-xs text-gray-500">
                            +{request.items.length - 2}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => viewInvoice(request)}
                      className="py-2 bg-green-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2"
                    >
                      <Receipt className="w-3 h-3" />
                      Facture
                    </button>
                    
                    {request.status === 'confirmed' ? (
                      <button
                        onClick={() => createProject(request)}
                        className="py-2 bg-orange-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2"
                      >
                        <FolderPlus className="w-3 h-3" />
                        Créer projet
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate(`/detailsdemande/${request.id}`)}
                        className="py-2 bg-purple-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2"
                      >
                        <FileText className="w-3 h-3" />
                        Détails
                      </button>
                    )}
                  </div>

                  {/* Toggle détails */}
                  <button
                    onClick={() => setExpandedCard(isExpanded ? null : request.id || null)}
                    className="w-full mt-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium flex items-center justify-center gap-2"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Moins d'infos
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Plus d'infos
                      </>
                    )}
                  </button>
                </div>

                {/* Détails expandables */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">Informations client</h4>
                    <div className="space-y-1 text-xs text-gray-600 mb-4">
                      <div><strong>Email:</strong> {request.client_email || 'N/A'}</div>
                      <div><strong>Téléphone:</strong> {request.client_phone || 'N/A'}</div>
                      <div><strong>Entreprise:</strong> {request.client_company || 'N/A'}</div>
                    </div>

                    <h4 className="text-xs font-bold text-gray-900 mb-2">Informations financières</h4>
                    <div className="space-y-1 text-xs text-gray-600 mb-4">
                      <div><strong>Sous-total:</strong> {formatPrice(request.subtotal)}</div>
                      {request.discount_percentage && (
                        <div className="text-green-600">
                          <strong>Réduction:</strong> {request.discount_percentage}% ({formatPrice(request.discount_amount)})
                        </div>
                      )}
                      {request.tva_percentage && (
                        <div><strong>TVA:</strong> {request.tva_percentage}% ({formatPrice(request.tva_amount)})</div>
                      )}
                    </div>

                    {request.items && request.items.length > 0 && (
                      <>
                        <h4 className="text-xs font-bold text-gray-900 mb-2">Items commandés</h4>
                        <div className="space-y-2">
                          {request.items.map((item, index) => (
                            <div key={index} className="bg-white p-2 rounded-lg">
                              <div className="text-xs font-semibold text-gray-900">
                                {item.service_name || item.pack_name || `Item ${index + 1}`}
                              </div>
                              <div className="text-[10px] text-gray-600 mt-1">
                                {item.quantity > 1 && `Qté: ${item.quantity} • `}
                                {item.pages && `Pages: ${item.pages} • `}
                                Prix: {formatPrice(item.total_price)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileDemandesClientsPage;
